import {
  JoinRoomTypes,
  JoinMemberRoomPayload,
  LeaveMemberRoomPayload,
  MemberRoomJoinFailedPayload,
} from '@act/shared/socket-events';
import { actSocket } from './actSocket';

let lastSubscribedMemberId: string | null = null;
let notReadyRetryTimeout: ReturnType<typeof setTimeout> | null = null;

const NOT_READY_RETRY_MS = 250;
const NOT_READY_MAX_ATTEMPTS = 8;
const notReadyAttemptsByMember = new Map<string, number>();

/**
 * Emits JOIN_ROOM without updating local tracking.
 *
 * @param memberId Member user id.
 */
function emitJoinRoom(memberId: string): void {
  const payload: JoinMemberRoomPayload = { memberId };
  actSocket.emit(JoinRoomTypes.JOIN_ROOM, payload);
}

/**
 * Handles server join failures. Retries when rooms are not hydrated yet.
 *
 * @param payload Join failure payload from the API.
 */
export function handleMemberRoomJoinFailed(
  payload: MemberRoomJoinFailedPayload
): void {
  if (payload?.reason !== 'not_ready' || !payload.memberId) {
    return;
  }
  if (lastSubscribedMemberId !== payload.memberId) {
    return;
  }
  const attempts = (notReadyAttemptsByMember.get(payload.memberId) ?? 0) + 1;
  if (attempts > NOT_READY_MAX_ATTEMPTS) {
    notReadyAttemptsByMember.delete(payload.memberId);
    return;
  }
  notReadyAttemptsByMember.set(payload.memberId, attempts);
  if (notReadyRetryTimeout) {
    clearTimeout(notReadyRetryTimeout);
  }
  notReadyRetryTimeout = setTimeout(() => {
    notReadyRetryTimeout = null;
    emitJoinRoom(payload.memberId);
  }, NOT_READY_RETRY_MS * attempts);
}

/**
 * Asks the API to subscribe this tab to a member's realtime rooms.
 * Idempotent; assigned caseload is already joined on connect.
 *
 * @param memberId Member user id, or null/empty to no-op.
 */
export function subscribeToMemberRoom(memberId: string | null | undefined): void {
  if (!memberId) {
    return;
  }

  if (lastSubscribedMemberId && lastSubscribedMemberId !== memberId) {
    unsubscribeFromMemberRoom(lastSubscribedMemberId);
  }

  notReadyAttemptsByMember.delete(memberId);
  lastSubscribedMemberId = memberId;
  emitJoinRoom(memberId);
}

/**
 * Asks the API to leave a non-caseload overflow member room.
 * Assigned caseload rooms are ignored by the server.
 *
 * @param memberId Member user id to leave.
 */
export function unsubscribeFromMemberRoom(
  memberId: string | null | undefined
): void {
  if (!memberId) {
    return;
  }

  if (notReadyRetryTimeout) {
    clearTimeout(notReadyRetryTimeout);
    notReadyRetryTimeout = null;
  }
  notReadyAttemptsByMember.delete(memberId);

  const payload: LeaveMemberRoomPayload = { memberId };
  actSocket.emit(JoinRoomTypes.LEAVE_ROOM, payload);

  if (lastSubscribedMemberId === memberId) {
    lastSubscribedMemberId = null;
  }
}

/**
 * Re-subscribes the currently selected member after socket rooms are ready.
 * Prefer calling this on SOCKET_INITIALIZED (not bare connect).
 *
 * @param memberId Currently selected member id from Redux, if any.
 */
export function resubscribeSelectedMemberRoom(
  memberId: string | null | undefined
): void {
  if (!memberId) {
    return;
  }
  lastSubscribedMemberId = null;
  subscribeToMemberRoom(memberId);
}

/**
 * Clears local subscription tracking (e.g. on logout). Does not emit leave.
 */
export function resetMemberRoomSubscriptionTracking(): void {
  if (notReadyRetryTimeout) {
    clearTimeout(notReadyRetryTimeout);
    notReadyRetryTimeout = null;
  }
  notReadyAttemptsByMember.clear();
  lastSubscribedMemberId = null;
}
